import {
  MAIL_ASSIST_BUSY,
  MAIL_ASSIST_DISABLED,
  type MailAssistEvent,
  type MailAssistResult,
  type MailAssistTask,
} from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CalendarDays,
  Check,
  Cpu,
  ListChecks,
  Loader2,
  PenLine,
  Power,
  Reply,
  ShieldAlert,
  Sparkles,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'

/** Mail-KI-Helfer (#782, Mockup mockup_782_lesesheet/_zustaende). Local AI
 * only; the answer lives in memory (React Query cache) and is rendered as
 * plain text nodes — never as HTML. Protected mail (#524): no request, no
 * buttons, only the notice. */

export const mailAssistStatusKey = ['mail-assist-status'] as const

type ApiError = Error & { status?: number; code?: string }

function parseDay(date: string): Date {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** „Fr 02.10." — weekday without the trailing dot. */
export function formatAssistDay(date: string): string {
  const day = parseDay(date)
  const weekday = day.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')
  const dm = day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
  return `${weekday} ${dm}`
}

/** Start/end for „+ Kalender": the named time or 09:00, one hour long. */
export function assistEventTimes(event: MailAssistEvent): { startTime: string; endTime: string } {
  const start = parseDay(event.date)
  const [h, min] = (event.time ?? '09:00').split(':').map(Number)
  start.setHours(h, min, 0, 0)
  const end = new Date(start.getTime() + 60 * 60_000)
  return { startTime: start.toISOString(), endTime: end.toISOString() }
}

function LocalBadge({ muted = false }: { muted?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold',
        muted
          ? 'border-border-default text-text-tertiary'
          : 'border-success/40 bg-success/10 text-success',
      )}
    >
      <Cpu size={11} aria-hidden="true" />
      lokale KI
    </span>
  )
}

function CardHead({ paused = false }: { paused?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-lg',
          paused ? 'bg-bg-overlay text-text-tertiary' : 'bg-accent/15 text-accent',
        )}
      >
        {paused ? (
          <Power size={15} aria-hidden="true" />
        ) : (
          <Sparkles size={15} aria-hidden="true" />
        )}
      </span>
      <span className={cn('flex-1 text-sm font-semibold', paused && 'text-text-secondary')}>
        {paused ? 'KI-Helfer pausiert' : 'KI-Helfer'}
      </span>
      <LocalBadge muted={paused} />
    </div>
  )
}

export function MailAiProtectedNotice() {
  return (
    <div
      className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/5 p-3"
      data-testid="mail-ai-protected"
    >
      <ShieldAlert size={16} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
      <div className="text-xs text-text-secondary">
        <p className="font-semibold text-warning">KI-Helfer aus – Kundendaten-Postfach</p>
        Keine Kurzfassung, keine erkannten Aufgaben, kein Antwortvorschlag. Die KI bekommt Mails aus
        geschützten Konten und Ordnern grundsätzlich nicht zu sehen.
      </div>
    </div>
  )
}

function LoadingCard() {
  return (
    <section
      className="space-y-3 rounded-2xl border border-accent/30 bg-accent/5 p-3"
      aria-busy="true"
      data-testid="mail-ai-loading"
    >
      <CardHead />
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Loader2 size={16} className="animate-spin text-accent" aria-hidden="true" />
        KI liest die Mail … (lokal, ca. 10 s)
      </div>
      <div className="space-y-2" aria-hidden="true">
        <div className="h-3 w-11/12 animate-pulse rounded-full bg-bg-overlay" />
        <div className="h-3 w-9/12 animate-pulse rounded-full bg-bg-overlay" />
        <div className="h-3 w-10/12 animate-pulse rounded-full bg-bg-overlay" />
        <div className="h-10 w-full animate-pulse rounded-xl bg-bg-overlay/70" />
      </div>
      <p className="text-[11px] text-text-tertiary">
        Lesen und Antworten gehen währenddessen normal weiter.
      </p>
    </section>
  )
}

function PausedCard({ busy, onRetry }: { busy: boolean; onRetry: () => void }) {
  return (
    <section
      className="space-y-2 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-3"
      data-testid="mail-ai-paused"
    >
      <CardHead paused />
      <p className="text-xs text-text-secondary">
        {busy
          ? 'Die lokale KI ist gerade ausgelastet. Bitte gleich noch einmal versuchen.'
          : 'Die lokale KI auf darkserver antwortet gerade nicht. Es gibt bewusst keinen Ausweich auf eine externe KI.'}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Erneut versuchen
      </Button>
    </section>
  )
}

function DetectedRow({
  icon,
  title,
  meta,
  actionLabel,
  done,
  pending,
  onAction,
}: {
  icon: ReactNode
  title: string
  meta: string
  actionLabel: string
  done: boolean
  pending: boolean
  onAction: () => void
}) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border-subtle bg-bg-elevated/60 px-2.5 py-2">
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{title}</span>
        <span className="block text-[11px] text-text-tertiary">{meta}</span>
      </span>
      <button
        type="button"
        onClick={onAction}
        disabled={done || pending}
        className={cn(
          'min-h-8 shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium',
          done
            ? 'border-success/40 text-success'
            : 'border-accent/30 text-accent hover:bg-accent/10',
          pending && 'opacity-60',
        )}
      >
        {done ? (
          <span className="inline-flex items-center gap-1">
            <Check size={12} aria-hidden="true" />
            angelegt
          </span>
        ) : (
          actionLabel
        )}
      </button>
    </div>
  )
}

export function MailAiResultCard({
  result,
  subject,
  onUseDraft,
}: {
  result: MailAssistResult
  subject: string
  onUseDraft: (draft: string) => void
}) {
  const qc = useQueryClient()
  const [done, setDone] = useState<Record<string, boolean>>({})
  const mark = (key: string) => setDone((prev) => ({ ...prev, [key]: true }))
  const createTask = useMutation({
    mutationFn: (input: { key: string; title: string; dueDate: string | null; notes?: string }) =>
      api.createTask({
        title: input.title,
        dueDate: input.dueDate,
        description: input.notes ?? null,
      }),
    onSuccess: (_data, input) => {
      mark(input.key)
      qc.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Aufgabe angelegt')
    },
    onError: (err: Error) => toast.error(err.message || 'Aufgabe konnte nicht angelegt werden'),
  })
  const createMeeting = useMutation({
    mutationFn: (input: { key: string; event: MailAssistEvent }) =>
      api.createMeeting({ title: input.event.title, ...assistEventTimes(input.event) }),
    onSuccess: (_data, input) => {
      mark(input.key)
      qc.invalidateQueries({ queryKey: ['meetings'] })
      toast.success('Termin im Kalender angelegt')
    },
    onError: (err: Error) => toast.error(err.message || 'Termin konnte nicht angelegt werden'),
  })
  const taskMeta = (task: MailAssistTask) =>
    task.due ? `Aufgabe · fällig ${formatAssistDay(task.due)}` : 'Aufgabe'
  const eventMeta = (event: MailAssistEvent) =>
    `Termin · ${formatAssistDay(event.date)}${event.time ? `, ${event.time}` : ''}`
  const replyTask = `Antworten: ${subject || '(Kein Betreff)'}`.slice(0, 200)

  return (
    <section
      className="space-y-3 rounded-2xl border border-accent/30 bg-accent/5 p-3"
      data-testid="mail-ai-card"
    >
      <CardHead />
      <div>
        <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-text-tertiary">
          Kurzfassung
        </p>
        <ul className="list-disc space-y-0.5 pl-4 text-sm leading-relaxed marker:text-accent">
          {result.summary.map((line, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static list per answer
            <li key={index}>{line}</li>
          ))}
        </ul>
      </div>
      {(result.tasks.length > 0 || result.events.length > 0) && (
        <div>
          <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-text-tertiary">
            Erkannt
          </p>
          <div className="space-y-1.5">
            {result.tasks.map((task, index) => {
              const key = `task-${index}`
              return (
                <DetectedRow
                  key={key}
                  icon={
                    <ListChecks size={16} className="shrink-0 text-accent" aria-hidden="true" />
                  }
                  title={task.title}
                  meta={taskMeta(task)}
                  actionLabel="+ Aufgabe"
                  done={!!done[key]}
                  pending={createTask.isPending && createTask.variables?.key === key}
                  onAction={() => createTask.mutate({ key, title: task.title, dueDate: task.due })}
                />
              )
            })}
            {result.events.map((event, index) => {
              const key = `event-${index}`
              return (
                <DetectedRow
                  key={key}
                  icon={
                    <CalendarDays size={16} className="shrink-0 text-info" aria-hidden="true" />
                  }
                  title={event.title}
                  meta={eventMeta(event)}
                  actionLabel="+ Kalender"
                  done={!!done[key]}
                  pending={createMeeting.isPending && createMeeting.variables?.key === key}
                  onAction={() => createMeeting.mutate({ key, event })}
                />
              )
            })}
          </div>
        </div>
      )}
      {result.replyDraft && (
        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2">
            <p className="text-2xs font-semibold uppercase tracking-wider text-text-tertiary">
              Antwortvorschlag
            </p>
            <span className="inline-flex items-center gap-1 text-2xs font-semibold text-warning">
              <PenLine size={11} aria-hidden="true" />
              Entwurf · wird nie automatisch gesendet
            </span>
          </div>
          <div className="whitespace-pre-wrap rounded-xl border border-dashed border-border-default bg-bg-base/60 p-2.5 text-sm leading-relaxed text-text-secondary">
            {result.replyDraft}
          </div>
        </div>
      )}
      <div className="flex gap-1.5">
        {result.replyDraft && (
          <Button
            className="min-w-0 flex-1 gap-1.5 px-2.5"
            onClick={() => onUseDraft(result.replyDraft)}
          >
            <Reply size={16} className="shrink-0" aria-hidden="true" />
            <span className="truncate">Entwurf übernehmen</span>
          </Button>
        )}
        <Button
          variant="secondary"
          className={cn('shrink-0 gap-1.5 px-2.5', !result.replyDraft && 'flex-1')}
          disabled={!!done.reply || (createTask.isPending && createTask.variables?.key === 'reply')}
          onClick={() =>
            createTask.mutate({
              key: 'reply',
              title: replyTask,
              dueDate: null,
              notes: result.summary.map((line) => `• ${line}`).join('\n'),
            })
          }
        >
          {/* Icon only from 400 px: at 390 px „Entwurf übernehmen" keeps its full label. */}
          {done.reply ? (
            <Check size={16} className="hidden min-[400px]:block" aria-hidden="true" />
          ) : (
            <ListChecks size={16} className="hidden min-[400px]:block" aria-hidden="true" />
          )}
          Als Aufgabe
        </Button>
      </div>
      <p className="text-[11px] leading-4 text-text-tertiary">
        Läuft auf darkserver (Qwen, lokal). Mailtext verlässt den Server nicht und wird nicht
        gespeichert. „Entwurf übernehmen“ öffnet das Antwortfenster – senden tust du.
      </p>
    </section>
  )
}

export function MailAiHelper({
  accountId,
  messageId,
  subject,
  protectedFlag,
  online,
  onUseDraft,
}: {
  accountId: string
  messageId: string
  subject: string
  protectedFlag: boolean
  online: boolean
  onUseDraft: (draft: string) => void
}) {
  const status = useQuery({
    queryKey: mailAssistStatusKey,
    queryFn: () => api.getMailAssistStatus(),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: online,
  })
  const enabled = status.data?.enabled === true
  const assist = useQuery({
    queryKey: ['mail-assist', accountId, messageId],
    queryFn: ({ signal }) => api.mailAssist(accountId, messageId, signal),
    // #524: a protected mail never produces a request — not even a failing one.
    enabled: enabled && online && !protectedFlag,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  if (!enabled) return null
  if (protectedFlag) return <MailAiProtectedNotice />
  if (!online) return null
  if (assist.isPending || assist.isFetching) return <LoadingCard />
  if (assist.isError) {
    const err = assist.error as ApiError
    if (err.code === 'MAIL_PROTECTED') return <MailAiProtectedNotice />
    if (err.code === MAIL_ASSIST_DISABLED) return null
    return <PausedCard busy={err.code === MAIL_ASSIST_BUSY} onRetry={() => assist.refetch()} />
  }
  return <MailAiResultCard result={assist.data.result} subject={subject} onUseDraft={onUseDraft} />
}
